/**
 * The list row and the transcript bubble that browse surfaces render. The bubble is
 * the thread's own turn, so a ticket reads the same wherever it is browsed and after
 * it is loaded.
 */

import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { User, Headset, Sparkles, type LucideIcon } from 'lucide-react';
import type { ConversationSummary } from '@/services/message.service';
import type { Message } from '@/types/shadow.types';
import { TurnBubble, TurnLabel } from '../ui/turn';
import { contactLabel, fmtWhen } from './conversation-format';

// ─── List row ──────────────────────────────────────────────────────────────

interface ListRowProps {
  title: string;
  when: string | null;
  subject?: string | null;
  preview?: string | null;
  id?: string;
  channel?: string | null;
  /** The last line in place of the id and channel, for a row that is not a ticket. */
  meta?: ReactNode;
  selected: boolean;
  onSelect: () => void;
}

export const ListRow = ({ title, when, subject, preview, id, channel, meta, selected, onSelect }: ListRowProps) => (
  <button
    type='button'
    onClick={onSelect}
    aria-current={selected || undefined}
    className={cn(
      'flex w-full cursor-pointer flex-col gap-0.5 border-b border-gray-300 px-4 py-3 text-left transition-colors hover:bg-gray-100/60',
      selected && 'bg-gray-100 shadow-[inset_2px_0_0_var(--color-ink)] hover:bg-gray-100'
    )}
  >
    <span className='flex items-baseline justify-between gap-2'>
      <span className='truncate text-[13px] leading-4.5 font-medium text-text-body'>{title}</span>
      <span className='shrink-0 text-[11px] leading-4 text-gray-800'>{fmtWhen(when)}</span>
    </span>
    {(subject || preview) && <span className='line-clamp-1 text-xs leading-4 text-gray-800'>{subject || preview}</span>}
    {meta ?? (
      <span className='text-[11px] leading-4 text-gray-800 capitalize'>
        <span className='font-mono'>#{id}</span>
        {channel && ` · ${channel}`}
      </span>
    )}
  </button>
);

export const ConvItem = ({ conv, selected, onSelect }: { conv: ConversationSummary; selected: boolean; onSelect: () => void }) => (
  <ListRow
    title={contactLabel(conv)}
    when={conv.lastMessageAt}
    subject={conv.subject}
    preview={conv.lastMessage?.content}
    id={String(conv.conversationId)}
    channel={conv.channel}
    selected={selected}
    onSelect={onSelect}
  />
);

// ─── Message preview bubble ────────────────────────────────────────────────

export type TranscriptMessage = Pick<Message, 'role' | 'content' | 'createdAt'>;

const ROLE_LABEL: Record<Message['role'], string> = { user: 'Customer', assistant: 'AI', agent: 'Agent' };
const ROLE_ICON: Record<Message['role'], LucideIcon> = { user: User, assistant: Sparkles, agent: Headset };

export const MsgBubble = ({ m }: { m: TranscriptMessage }) => {
  const isCustomer = m.role === 'user';
  const when = fmtWhen(m.createdAt);
  return (
    <div className={cn('flex w-full', isCustomer ? 'justify-start' : 'justify-end')}>
      <div className={cn('flex min-w-0 max-w-[80%] flex-col gap-1', isCustomer ? 'items-start' : 'items-end')}>
        <TurnLabel icon={ROLE_ICON[m.role]}>
          <span className='font-medium'>{ROLE_LABEL[m.role]}</span>
          {when && (
            <>
              <span>·</span>
              <span>{when}</span>
            </>
          )}
        </TurnLabel>
        {/* wrap-anywhere breaks an unbreakable string (a tracking URL, a signature
            image link) inside the bubble instead of letting it widen the row. */}
        <TurnBubble tone={isCustomer ? 'customer' : 'agent'} className='whitespace-pre-wrap wrap-anywhere'>
          {m.content}
        </TurnBubble>
      </div>
    </div>
  );
};
